import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Logo } from '@/components/Logo';
import { ArrowLeft, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErr('Passwords don\u2019t match.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => nav('/login', { replace: true }), 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Reset failed. Try requesting a new link.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="reset-page">
      <div className="hidden lg:flex bg-ink text-white p-12 relative kc-noise">
        <div className="absolute inset-0 gold-sheen pointer-events-none"/>
        <div className="relative flex flex-col justify-between w-full">
          <Logo invert/>
          <div>
            <h1 className="font-display uppercase text-5xl xl:text-6xl leading-none">Reset.<br/><span className="text-[color:var(--kc-gold)]">Pick a new one.</span></h1>
            <p className="mt-4 text-white/80 max-w-md">Choose something you'll remember. 8 characters minimum.</p>
          </div>
          <div/>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-kc hover:text-ink mb-6"><ArrowLeft size={12}/> Back to login</Link>
          <div className="lg:hidden mb-6"><Logo/></div>

          {!token && (
            <div className="text-center" data-testid="reset-no-token">
              <div className="w-14 h-14 mx-auto rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mb-4">
                <AlertCircle size={28} className="text-[color:var(--kc-danger)]"/>
              </div>
              <h2 className="font-display uppercase text-3xl">Missing reset link.</h2>
              <p className="mt-3 text-muted-kc">This page needs a reset token in the URL. Go request a new reset link.</p>
              <Link to="/forgot-password" className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold w-full">Request new link</Link>
            </div>
          )}

          {token && done && (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-gold-soft border-2 border-[color:var(--kc-gold)] flex items-center justify-center mb-4">
                <CheckCircle2 size={28} className="text-[color:var(--kc-gold-2)]"/>
              </div>
              <h2 className="font-display uppercase text-3xl">Password updated.</h2>
              <p className="mt-3 text-muted-kc">Taking you to login…</p>
            </div>
          )}

          {token && !done && (
            <form onSubmit={submit} data-testid="reset-form">
              <h2 className="font-display uppercase text-3xl md:text-4xl">Set a new password</h2>
              <p className="mt-1 text-muted-kc text-sm">At least 8 characters.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1">New password</label>
                  <div className="relative">
                    <input type={show ? 'text' : 'password'} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-11 px-3 pr-10 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="reset-pw-input" autoComplete="new-password"/>
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-kc">{show ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Confirm</label>
                  <input type={show ? 'text' : 'password'} required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full h-11 px-3 rounded-[var(--kc-radius-sm)] border border-kc-strong focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="reset-confirm-input" autoComplete="new-password"/>
                </div>
                {err && <div className="text-sm text-[color:var(--kc-danger)]" data-testid="reset-error">{err}</div>}
                <button type="submit" disabled={loading} className="w-full h-11 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 disabled:opacity-60" data-testid="reset-submit">{loading ? 'Updating…' : 'Update password'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
